import { CountryTypes, BusinessTypes, SourceTypes } from "../types";

import { apiSlice } from "@/redux/apiSlice";
import {
  MemberResponseTypes,
  MemberTabletypes,
  MemberTableDatatypes,
  MemberTableResponsetypes,
  MemberInputTypes,
  MemberInput,
  ActiveMembershipPlan,
  MembershipFacility,
  LinkedMemberMembership,
  MemberFacilityCredit,
} from "@/types/member";
import { ApiResponse, TableResponse, User } from "@/types";
import { MembershipStatus } from "@/types/enums";
const { VITE_DEFAULT_ORG } = import.meta.env;

type ActiveMemberships = TableResponse<ActiveMembershipPlan>;
type ActiveMembershipsFacilities = TableResponse<MembershipFacility>;

interface UpdateResponse {
  status_code: number;
  detail: string;
}

type Users = User & { own_member_id: string };

const userToken = localStorage.getItem("userToken");

export const MemberAPi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMemberCount: builder.query<{ total_members: number }, number>({
      query: (org_id) => ({
        url: `/member/count/${org_id}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Members"],
    }),
    getCountries: builder.query<CountryTypes[], void>({
      query: () => ({
        url: "/countries",
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
    }),
    getAllSource: builder.query<SourceTypes[], void>({
      query: () => ({
        url: "/sources",
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
    }),
    getAllBusinesses: builder.query<BusinessTypes[], number>({
      query: (org_id) => ({
        url: `/member/business/${org_id}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
    }),
    getAllMember: builder.query<MemberTabletypes, MemberInput>({
      query: (searchCretiria) => ({
        url: `/member?org_id=${searchCretiria.org_id}&${searchCretiria.query}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (
        response: MemberTableResponsetypes
      ): MemberTabletypes => ({
        ...response,
        data: response.data.map((record) => ({
          ...record,
          coaches: record.coaches.map((coach) => ({
            id: coach.id,
            name: coach.coach_name, // Rename 'coach_name' to 'name'
          })),
        })),
      }),
      providesTags: ["Members"],
    }),
    getMemberById: builder.query<
      MemberTableDatatypes,
      { org_id: number; id: number; auth_token?: string }
    >({
      query: (member) => ({
        url: `/member/${member.id}?org_id=${member.org_id}`,
        method: "GET",

        headers: {
          Accept: "application/json",
          Authorization:
            member?.auth_token !== "" ? member?.auth_token : userToken || "",
        },
      }),
      providesTags: ["Members", "Invoice", "Transaction"],
    }),
    AddMember: builder.mutation<ApiResponse, MemberInputTypes>({
      query: (memberdata) => ({
        url: "/member",
        method: "POST",
        body: memberdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Members"],
    }),
    updateMember: builder.mutation<
      ApiResponse,
      MemberInputTypes & { id: number }
    >({
      query: (memberdata) => ({
        url: `/member/${memberdata.id}`,
        method: "PUT",
        body: memberdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Members"],
    }),
    deleteMember: builder.mutation<
      MemberResponseTypes,
      { org_id: number; id: number }
    >({
      query: (member) => ({
        url: `/member/${member.id}?org_id=${member.org_id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Members"],
    }),
    getMembersList: builder.query<
      { value: number; label: string; email: string }[],
      { id: number; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/member/list/${id}${query !== "" ? "?" + query : ""}`,
        headers: {
          Accept: "application/json",
        },
        providesTags: ["Members"],
      }),
      transformResponse: (
        response: { id: number; name: string; email: string }[]
      ) =>
        response.map((item: { id: number; name: string; email: string }) => ({
          value: item.id,
          label: item.name,
          email: item.email,
        })),
    }),

    getMemberMembership: builder.query<ActiveMemberships, MemberInput>({
      query: (memberdata) => ({
        url: `/member/${memberdata.member_id}/memberships?${memberdata.query}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Members", "Invoice", "Transaction"],
    }),

    getMembershipFacility: builder.query<
      ActiveMembershipsFacilities,
      MemberInput
    >({
      query: (memberdata) => ({
        url: `/member/${memberdata.member_id}/facilities?${memberdata.query}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Members", "Invoice", "Transaction"],
    }),
    updateMembershipFacilityCredits: builder.mutation<
      UpdateResponse,
      {
        source_id: number;
        source_type_id: number;
        facility_id: number;
        credits: number;
        member_id: number;
      }
    >({
      query: (memberdata) => ({
        url: `/member/${memberdata.member_id}/update-credits`,
        method: "PUT",
        body: memberdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Members", "Invoice", "Transaction"],
    }),
    getMemberInSystem: builder.query<
      Users[],
      {
        query: string;
        limit?: number;
        offset?: number;
        sort_order?: string;
        sort_key?: string;
        type?: string;
        status?: string;
        org_id?: number;
      }
    >({
      query: (memberdata) => {
        const params = new URLSearchParams();
        params.append("org_id", memberdata.org_id ?? VITE_DEFAULT_ORG);
        // If search query is empty, use default filters
        if (!memberdata.query || memberdata.query.trim() === "") {
          if (memberdata.limit)
            params.append("limit", memberdata.limit.toString());
          if (memberdata.offset)
            params.append("offset", memberdata.offset.toString());
          if (memberdata.sort_order)
            params.append("sort_order", memberdata.sort_order);
          if (memberdata.sort_key)
            params.append("sort_key", memberdata.sort_key);
          if (memberdata.type) params.append("type", memberdata.type);
        } else {
          // If search query exists, only use search_key
          params.append("search_key", memberdata.query);
          params.append("sort_order", memberdata.sort_order ?? "desc");
          params.append("sort_key", memberdata.sort_key ?? "id");
        }

        return {
          url: `/member?${params.toString()}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        };
      },
      transformResponse: (response: { data: Users[] }) => response.data, // Transform the response to return only the data
      providesTags: ["Members"],
    }),
    updateMembershipStatus: builder.mutation<
      UpdateResponse,
      LinkedMemberMembership
    >({
      query: (memberdata) => ({
        url: `/member/${memberdata.client_id}/membership-status`,
        method: "PATCH",
        body: memberdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Members", "Invoice", "Transaction"],
    }),
    getAllMemberForExport: builder.query<
      MemberTabletypes,
      { org_id: number; filterQuery?: string }
    >({
      query: ({ org_id, filterQuery }) => ({
        url: `/member?org_id=${org_id}&offset=0&sort_key=id&sort_order=desc${
          filterQuery ? `&${filterQuery}` : ""
        }`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (
        response: MemberTableResponsetypes
      ): MemberTabletypes => ({
        ...response,
        data: response.data.map((record) => ({
          ...record,
          coaches: record.coaches.map((coach) => ({
            id: coach.id,
            name: coach.coach_name, // Rename 'coach_name' to 'name'
          })),
        })),
      }),
      providesTags: ["Members"],
    }),
    getMemberFacilityCredits: builder.query<
      TableResponse<MemberFacilityCredit>,
      {
        member_id: number;
        org_id: number;
        facility_id?: number | null;
        sort_order?: string | null;
        sort_key?: string | null;
        limit?: number | null;
        offset?: number | null;
      }
    >({
      query: ({ member_id, ...params }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
        return {
          url: `/members/${member_id}/facility-credits?${queryParams.toString()}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        };
      },
      providesTags: ["Members"],
    }),
  }),
});

export const {
  useGetMemberCountQuery,
  useGetAllBusinessesQuery,
  useGetAllMemberQuery,
  useGetMembersListQuery,
  useGetMemberByIdQuery,
  useAddMemberMutation,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
  useGetMemberMembershipQuery,
  useGetMembershipFacilityQuery,
  useUpdateMembershipFacilityCreditsMutation,
  useGetMemberInSystemQuery,
  useUpdateMembershipStatusMutation,
  useLazyGetMemberByIdQuery,
  useLazyGetAllMemberForExportQuery,
  useGetMemberFacilityCreditsQuery,
  useLazyGetMemberFacilityCreditsQuery,
} = MemberAPi;
